import { DAY_MS } from "./freshness.js";
import { buildCursorPath, type FetchPage } from "./asyncList.js";

// The read window for the Metabolic card, owned in one place because it is
// read from two mounts — apps/mobile's useMetabolic and apps/web-rnw's
// useMetabolicWeb — which must show the same rows for the same athlete. The
// two hooks stay separate (they can't share `apiFetch`, whose implementation
// is deliberately per-mount), but the WINDOW and the query they build are not
// a mount concern, and when they were written out by hand in both files the
// two could drift silently: nothing would fail, the web card would just show
// a different span than the phone.
//
// ⚠️ OPEN, and deliberately not decided here: 30 is what both mounts always
// hardcoded, but Progress's own window default is 90
// (ui/Progress/progressWindow.ts DEFAULT_WINDOW_DAYS), consumed by seven
// panels. Nobody ever chose 30 against 90 — the census entry
// CN-metabolic-window calls it drift, not design. Naming the constant is
// half the fix; the other half is a product call about how much history the
// Metabolic card should show, so it is left to Antenore rather than settled
// by whoever touched the file. Do NOT quietly align it to 90: that changes
// what the athlete sees.
export const METABOLIC_WINDOW_DAYS = 30;

// Page cap. The API caps its own page anyway; this keeps the card light and
// makes the ceiling visible at the call site rather than implicit.
export const METABOLIC_READ_LIMIT = 500;

// Query string for GET /api/me/glucose and /api/me/nutrition — the same
// window and cap for both endpoints, which is the point.
//
// `nowMs` is injectable so the window can be pinned without stubbing the
// clock — apps/mobile/__tests__/metabolicRead.test.ts does exactly that.
// Production callers omit it.
export const metabolicReadQuery = (nowMs: number = Date.now()): string => {
  const since = new Date(nowMs - METABOLIC_WINDOW_DAYS * DAY_MS).toISOString();
  return `since=${encodeURIComponent(since)}&limit=${METABOLIC_READ_LIMIT}`;
};

// HC-22: the card asked for the 30-day window above but only ever read ONE
// page of METABOLIC_READ_LIMIT (500) rows, cursor ignored. At the measured
// CGM cadence (288 readings/day) that's ~1.7 days shown under a "30 days"
// label, a truth defect, not a performance one. The fix is to walk the
// cursor with `paginate()` (packages/shared/src/asyncList.ts) rather than
// hand-roll a second do/while (HR-10's lesson lives one file up in
// data-volume.md: an unbounded walk on a per-user partition is the same
// shape that already caused a 500 in production once).
//
// `metabolicPagePath` mirrors `measurementsPagePath` (measurementsPage.ts):
// same since/limit query, plus `&cursor=` when the walk is past page one.
export function metabolicPagePath(
  path: string,
  cursor: string | null,
  nowMs: number,
): string {
  return buildCursorPath(path, metabolicReadQuery(nowMs), cursor);
}

/** The per-platform seam, same reasoning as measurementsPage.ts's
 *  PageFetcher: mobile and web each bring their own audited apiFetch.
 *  Generic here (unlike the concrete PageFetcher next door) because this
 *  one seam serves TWO row shapes, glucose and nutrition, off the same
 *  window/cursor logic; a concrete type would need two copies to gain
 *  nothing a type parameter doesn't already give it. Rows arrive exactly
 *  as the server sends them: no zod validation is added here, matching
 *  the trust level `useMetabolic`/`useMetabolicWeb` already had. */
export type MetabolicPageFetcher<T> = (
  path: string,
) => Promise<{ items: T[]; cursor: string | null }>;

export function makeMetabolicFetchPage<T>(
  path: string,
  fetchJson: MetabolicPageFetcher<T>,
  nowMs: number,
): FetchPage<T, string> {
  return async (cursor) => {
    const res = await fetchJson(metabolicPagePath(path, cursor, nowMs));
    return { items: res.items ?? [], cursor: res.cursor };
  };
}

// Page-count ceiling for `paginate()`. Measured: a ~29-day CGM block runs
// ~8,500 glucose rows at the 288/day cadence above, i.e. ~18 pages at
// METABOLIC_READ_LIMIT (500/page). 40 pages = a 20,000-row ceiling, a
// comfortable multiple over the measured volume with room for a denser
// sensor or a slightly wider window, while still capping the input per
// HR-10 (a per-user read must have a ceiling, not just a per-page limit).
export const METABOLIC_READ_MAX_PAGES = 40;
